// screens/marketplace/AdminCourseEnrollments.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReceiptText,
  RefreshCw,
  Search,
  Copy,
  Check,
  Send,
  Mail,
  Phone,
  User as UserIcon,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Section,
  Button,
  Input,
  EmptyState,
  LoadingBlock,
  useTokens,
} from "./shared";
import { listAdminEnrollments } from "./coursesManageApi";

const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

export default function AdminCourseEnrollments() {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState("PAID");
  const [search, setSearch] = useState("");

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const data = await listAdminEnrollments(authFetch, {
          status: status || undefined,
        });
        setEnrollments(data);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, status, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enrollments;
    return enrollments.filter((e) =>
      [
        e.course_title,
        e.username,
        e.contact_name,
        e.contact_email,
        e.contact_phone,
        e.telegram_username,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [enrollments, search]);

  const paidCount = enrollments.filter((e) => e.status === "PAID").length;

  return (
    <PageShell
      eyebrow="Courses"
      title="Enrollments"
      subtitle="Review buyers who purchased a course and accept their Telegram join request using the username shown here."
      actions={
        <Button
          variant="secondary"
          icon={RefreshCw}
          onClick={() => load({ silent: true })}
          disabled={refreshing}
        >
          Refresh
        </Button>
      }
    >
      <Section
        title="Enrollments"
        icon={ReceiptText}
        chip={`${filtered.length} · ${paidCount} paid`}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", gap: 6 }}>
            {STATUS_FILTERS.map((s) => {
              const active = status === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 999,
                    border: `1px solid ${active ? t.accent : t.border}`,
                    background: active ? t.accent : t.surfaceRaised,
                    color: active ? t.accentText : t.text,
                    cursor: "pointer",
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
            <Search
              size={14}
              color={t.muted}
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
            <Input
              placeholder="Search by name, email, or Telegram…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 36 }}
            />
          </div>
        </div>

        {loading ? (
          <LoadingBlock label="Loading enrollments…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ReceiptText}
            title="No enrollments match"
            description="Adjust the status filter or clear the search."
          />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {filtered.map((e) => (
              <EnrollmentRow key={e.id} enrollment={e} t={t} />
            ))}
          </div>
        )}
      </Section>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Row
   ══════════════════════════════════════════════════════════ */

const EnrollmentRow = ({ enrollment, t }) => {
  const [copiedField, setCopiedField] = useState(null);
  const paid = enrollment.status === "PAID";

  const copy = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // silent
    }
  };

  const statusColor = {
    PAID: { fg: t.success, bg: t.successBg },
    PENDING: { fg: t.warning, bg: t.warningBg },
    FAILED: { fg: t.error, bg: t.errorBg },
  }[enrollment.status] || { fg: t.muted, bg: t.subtle };

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${t.border}`,
        background: t.surfaceRaised,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Top row: course + status + amount */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ flex: 1, minWidth: 200 }}>
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 800,
              color: t.heading,
              letterSpacing: "-0.01em",
            }}
          >
            {enrollment.course_title}
          </div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>
            {enrollment.username} · {money(enrollment.amount)}
            {enrollment.payment_method && ` · ${enrollment.payment_method}`}
          </div>
        </div>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            padding: "4px 10px",
            borderRadius: 999,
            background: statusColor.bg,
            color: statusColor.fg,
          }}
        >
          {enrollment.status}
        </span>
      </div>

      {/* Contact + Telegram grid */}
      <div style={styles.contactGrid}>
        <CopyableField
          icon={UserIcon}
          label="Name"
          value={enrollment.contact_name}
          copied={copiedField === "name"}
          onCopy={() => copy("name", enrollment.contact_name)}
          t={t}
        />
        <CopyableField
          icon={Mail}
          label="Email"
          value={enrollment.contact_email}
          copied={copiedField === "email"}
          onCopy={() => copy("email", enrollment.contact_email)}
          t={t}
        />
        <CopyableField
          icon={Phone}
          label="Phone"
          value={enrollment.contact_phone}
          copied={copiedField === "phone"}
          onCopy={() => copy("phone", enrollment.contact_phone)}
          t={t}
        />
        <CopyableField
          icon={Send}
          label="Telegram"
          value={enrollment.telegram_username}
          copied={copiedField === "tg"}
          onCopy={() => copy("tg", enrollment.telegram_username)}
          t={t}
          highlight
        />
      </div>

      {/* Admin helper + Telegram open */}
      {paid && enrollment.telegram_username && (
        <div style={styles.helperNote(t)}>
          <AlertCircle size={13} />
          On Telegram, accept the join request from{" "}
          <strong style={{ color: t.heading }}>
            {enrollment.telegram_username}
          </strong>
          . Match the username to this row.
          {enrollment.telegram_link && (
            <>
              {" "}
              <a
                href={enrollment.telegram_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: t.accent,
                  fontWeight: 700,
                  textDecoration: "underline",
                  marginLeft: 4,
                }}
              >
                Open invite
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CopyableField = ({ icon: Icon, label, value, copied, onCopy, t, highlight }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 10,
      background: highlight ? `${t.accent}12` : t.subtle,
      border: `1px solid ${highlight ? `${t.accent}40` : t.border}`,
    }}
  >
    <Icon size={14} color={highlight ? t.accent : t.muted} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: t.muted,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: t.text,
          marginTop: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </div>
    </div>
    {value && (
      <button
        type="button"
        onClick={onCopy}
        title="Copy"
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          border: "none",
          background: copied ? t.successBg : "transparent",
          color: copied ? t.success : t.muted,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    )}
  </div>
);

const styles = {
  contactGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 8,
  },
  helperNote: (t) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 10,
    background: t.subtle,
    border: `1px solid ${t.border}`,
    fontSize: 12.5,
    color: t.text,
    lineHeight: 1.55,
  }),
};